'use client';

import * as React from 'react';

import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';

import { releaseBottomSpace, reserveBottomSpace } from './reserve-bottom-space';
import {
  findScrollPort,
  measureTopCover,
  releaseScrollPadding,
  reserveScrollPadding,
} from './reserve-scroll-padding';
import {
  SCROLL_POINT_ATTRIBUTE,
  findScrollPoint,
  resolveObserveTarget,
} from './scroll-point';
import { stickyBarVariants } from './sticky-bar.variants';

export { SCROLL_POINT_ATTRIBUTE, findScrollPoint, resolveObserveTarget };

export type StickyBarAppears = 'afterScrollPoint' | 'always';
export type StickyBarScreenEdge = 'top' | 'bottom';
export type StickyBarSpaceAbove = 'none' | 'header';
export type StickyBarShadow = 'none' | 'sm' | 'md' | 'lg';

export type StickyBarProps = {
  /** Content of the bar. */
  children?: React.ReactNode;
  /** Whether the bar waits for a scroll point or is docked from the start. */
  appears?: StickyBarAppears;
  /** Name of the block to scroll past before the bar appears. */
  showAfter?: string;
  /** Where the bar docks once it appears. */
  screenEdge?: StickyBarScreenEdge;
  /** Room between the bar and the top of the screen. */
  spaceAbove?: StickyBarSpaceAbove;
  /** Shadow cast over the page while the bar is docked. */
  shadow?: StickyBarShadow;
};

type StickyBarState = 'hidden' | 'shown' | 'inFlow';

/** True once the watched block's bottom edge has crossed above the viewport. */
export function hasScrolledPast(entry: IntersectionObserverEntry): boolean {
  const bottom = entry.boundingClientRect?.bottom;
  if (typeof bottom !== 'number') return false;
  const viewportTop = entry.rootBounds?.top ?? 0;
  return bottom <= viewportTop;
}

function resolveState({
  always,
  failOpen,
  isRevealed,
}: {
  always: boolean;
  failOpen: boolean;
  isRevealed: boolean;
}): StickyBarState {
  if (always) return 'shown';
  if (failOpen) return 'inFlow';
  return isRevealed ? 'shown' : 'hidden';
}

/**
 * Keeps scroll-padding in step with a top bar so jumps land below it. The
 * reservation is keyed by the bar element so hash jumps can leave out a bar
 * that will hide once the jump lands.
 */
function useReservedTopSpace(
  rootRef: React.RefObject<HTMLDivElement | null>,
  reserve: boolean,
  layout: { screenEdge: StickyBarScreenEdge; spaceAbove: StickyBarSpaceAbove },
): void {
  const { screenEdge, spaceAbove } = layout;
  React.useEffect(() => {
    const bar = rootRef.current;
    if (!reserve || !bar) return;
    const doc = bar.ownerDocument;
    const port = findScrollPort(bar);
    // Layout cover, not the transformed box: the slide-in is still running
    // on the first measure, and ResizeObserver does not see transform frames.
    const measure = () =>
      reserveScrollPadding(port, bar, measureTopCover(bar, port));
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== bar || event.propertyName !== 'transform') return;
      measure();
    };
    measure();
    bar.addEventListener('transitionend', onTransitionEnd);
    const Resize = (doc.defaultView as (Window & typeof globalThis) | null)
      ?.ResizeObserver;
    const resize = Resize ? new Resize(measure) : null;
    resize?.observe(bar);
    return () => {
      resize?.disconnect();
      bar.removeEventListener('transitionend', onTransitionEnd);
      releaseScrollPadding(port, bar);
    };
  }, [rootRef, reserve, screenEdge, spaceAbove]);
}

/** Keeps the page's bottom padding in step with the bar's height. */
function useReservedBottomSpace(
  rootRef: React.RefObject<HTMLDivElement>,
  reserve: boolean,
): void {
  React.useEffect(() => {
    const bar = rootRef.current;
    if (!reserve || !bar) return;
    const doc = bar.ownerDocument;
    const key = Symbol('sticky-bar');
    const measure = () =>
      reserveBottomSpace(doc, key, bar.getBoundingClientRect().height);
    measure();
    const Resize = (doc.defaultView as (Window & typeof globalThis) | null)
      ?.ResizeObserver;
    const resize = Resize ? new Resize(measure) : null;
    resize?.observe(bar);
    return () => {
      resize?.disconnect();
      releaseBottomSpace(doc, key);
    };
  }, [rootRef, reserve]);
}

/**
 * Root attributes read by in-page jumps (NavTabs, the Admin preview frame):
 *
 * - `data-hen-sticky-revealed`: `'true'` while the bar is on screen.
 * - `data-hen-sticky-after`: the Show after name, present only when the bar
 *   waits for a scroll point, so a jump can tell whether the bar will be on
 *   screen once it lands.
 * - `data-hen-sticky-edge`: `'top'` or `'bottom'`. Computed `top` can't tell
 *   them apart, because browsers resolve it to pixels for a bottom bar too.
 */
export const StickyBar = React.forwardRef<HTMLDivElement, StickyBarProps>(
  (
    {
      children,
      appears = 'afterScrollPoint',
      showAfter,
      screenEdge = 'top',
      spaceAbove = 'none',
      shadow = 'none',
    },
    ref,
  ) => {
    const always = appears === 'always';
    const markerId = always ? '' : (showAfter?.trim() ?? '');
    const hasMarkerName = markerId.length > 0;

    const rootRef = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(ref, () => rootRef.current as HTMLDivElement);

    const [isRevealed, setIsRevealed] = React.useState(false);
    const [markerMissing, setMarkerMissing] = React.useState(false);

    React.useEffect(() => {
      setIsRevealed(false);
      if (!hasMarkerName) {
        setMarkerMissing(false);
        return;
      }
      // The bar can be portaled into an iframe; search the document it renders in.
      const doc = rootRef.current?.ownerDocument ?? document;

      const view = doc.defaultView ?? window;
      // An observer from another window measures against that window's viewport.
      const Observer =
        (view as Window & typeof globalThis).IntersectionObserver ??
        IntersectionObserver;
      let intersection: IntersectionObserver | null = null;
      // `undefined` until the first lookup, so a missing block still applies.
      let watchedBlock: Element | null | undefined;
      let scrollTarget: Element | null = null;
      let frame = 0;

      // IntersectionObserver only reports visibility changes, so a jump from
      // below the screen to above it (End key, anchor link) is never
      // reported. Re-check on scroll, at most once per frame.
      const onScroll = () => {
        if (frame || !scrollTarget) return;
        frame = view.requestAnimationFrame(() => {
          frame = 0;
          if (!scrollTarget) return;
          setIsRevealed(scrollTarget.getBoundingClientRect().bottom <= 0);
        });
      };

      const unwatch = () => {
        intersection?.disconnect();
        intersection = null;
        watchedBlock = null;
        scrollTarget = null;
      };

      const watch = (block: Element) => {
        watchedBlock = block;
        scrollTarget = resolveObserveTarget(block);
        intersection = new Observer(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;
            setIsRevealed(hasScrolledPast(entry));
          },
          { threshold: 0 },
        );
        intersection.observe(scrollTarget);
      };

      // Blocks can unmount, remount, or change name after the bar finds
      // them (visibility rules, step changes, editor re-renders). A removed
      // block reads as scrolled past, so keep the lookup current instead of
      // resolving it once.
      const sync = () => {
        const found = findScrollPoint(markerId, doc);
        if (found === watchedBlock) return;
        unwatch();
        watchedBlock = found;
        setIsRevealed(false);
        setMarkerMissing(!found);
        if (found) watch(found);
      };

      sync();
      const mutations = new MutationObserver(sync);
      mutations.observe(doc.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [SCROLL_POINT_ATTRIBUTE],
      });
      doc.addEventListener('scroll', onScroll, {
        capture: true,
        passive: true,
      });

      return () => {
        unwatch();
        mutations.disconnect();
        doc.removeEventListener('scroll', onScroll, { capture: true });
        if (frame) view.cancelAnimationFrame(frame);
      };
    }, [hasMarkerName, markerId]);

    const failOpen = !hasMarkerName || markerMissing;
    const revealed = always || (isRevealed && !failOpen);
    const state = resolveState({ always, failOpen, isRevealed: revealed });

    useReservedBottomSpace(
      rootRef,
      state === 'shown' && screenEdge === 'bottom',
    );
    useReservedTopSpace(rootRef, state === 'shown' && screenEdge === 'top', {
      screenEdge,
      spaceAbove,
    });

    return (
      <div
        ref={rootRef}
        data-hen-id={COMPONENT_IDS.StickyBar}
        data-hen-sticky-revealed={revealed ? 'true' : 'false'}
        data-hen-sticky-after={hasMarkerName ? markerId : undefined}
        data-hen-sticky-edge={screenEdge}
        className={cn(
          stickyBarVariants({ state, screenEdge, spaceAbove, shadow }),
        )}
      >
        {children}
      </div>
    );
  },
);

StickyBar.displayName = 'StickyBar';
